"use client"

import { MagnifyingGlassIcon, XCircleIcon } from "@heroicons/react/20/solid"
import Spinner from "@src/components/Spinner"
import clsx from "clsx"
import {
  type ComponentProps,
  type ElementType,
  useEffect,
  useId,
  useRef,
} from "react"

type Props = ComponentProps<"input"> & {
  loading?: boolean
  onClear?: () => void
  icon?: ElementType
}

const SearchBar = ({
  placeholder = "Search tokens",
  className,
  onClear,
  autoFocus,
  loading,
  icon: Icon = MagnifyingGlassIcon,
  ...inputProps
}: Props) => {
  const id = useId()
  const ref = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (!autoFocus) return

    const shouldAutofocus = window.matchMedia(
      "(hover: hover) and (pointer: fine)"
    ).matches

    if (shouldAutofocus) {
      ref.current?.focus({ preventScroll: true })
    }
  }, [autoFocus])

  const displayValue = inputProps.value ?? inputProps.defaultValue ?? ""
  const hasValue = String(displayValue).length > 0
  const hasTrailingContent = loading || (hasValue && onClear)

  return (
    <div className={clsx("grid grid-cols-1", className)}>
      <label htmlFor={id} className="sr-only">
        {placeholder}
      </label>
      <input
        id={id}
        ref={ref}
        type="text"
        className={clsx(
          "-outline-offset-1 col-start-1 row-start-1 block w-full rounded-xl bg-white py-2.5 pl-9 font-medium text-gray-900 text-sm outline-1 outline-gray-200 placeholder:text-gray-400",

          hasTrailingContent ? "pr-9" : "pr-3",

          "focus-visible:-outline-offset-2 focus-visible:outline-2 focus-visible:outline-gray-900",

          "dark:bg-white/5 dark:text-white dark:outline-white/5 dark:focus-visible:outline-gray-400"
        )}
        placeholder={placeholder}
        {...inputProps}
      />
      <Icon
        className="pointer-events-none col-start-1 row-start-1 ml-2 size-5 self-center text-gray-400"
        aria-hidden
      />
      {loading ? (
        <div className="pointer-events-none col-start-1 row-start-1 mr-3 self-center justify-self-end">
          <Spinner size="sm" className="text-gray-500 dark:text-gray-400" />
        </div>
      ) : hasValue && onClear ? (
        <button
          type="button"
          className="col-start-1 row-start-1 mr-1 flex size-7 items-center justify-center self-center justify-self-end rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          onClick={() => {
            if (ref.current) {
              ref.current.value = ""
            }
            onClear()
          }}
          aria-label="Clear search"
        >
          <XCircleIcon className="size-5" aria-hidden />
        </button>
      ) : null}
    </div>
  )
}

export default SearchBar
